// Inline header search bar.
//
// A button styled as a search field that opens the shared search modal
// (`#search-modal`) on click OR focus, reusing Horizon's native predictive-search
// modal unchanged. We only swap the trigger (icon -> bar); the search engine,
// predictive results and modal markup are untouched.
//
// Guard: the native <dialog> restores focus to the element that opened it when it
// closes. With "open on focus" that would immediately reopen the modal, so we
// ignore focus (and re-clicks) for a short cooldown after the modal closes.
class SearchBar extends HTMLElement {
  connectedCallback() {
    /** @type {(HTMLElement & { showDialog?: () => void }) | null} */
    this.modal = document.getElementById('search-modal');
    this.requested = false;
    this.cooldown = false;

    this.addEventListener('click', this.open);
    this.addEventListener('focusin', this.open);

    if (this.modal) {
      this.modal.addEventListener('dialog:open', this.onOpen);
      this.modal.addEventListener('dialog:close', this.onClose);
    }
  }

  disconnectedCallback() {
    this.modal?.removeEventListener('dialog:open', this.onOpen);
    this.modal?.removeEventListener('dialog:close', this.onClose);
  }

  // `requested` collapses the click+focusin pair fired by a single interaction
  // into one showDialog() call; `cooldown` is a secondary guard.
  open = () => {
    if (this.cooldown || this.requested || typeof this.modal?.showDialog !== 'function') return;
    this.requested = true;
    this.modal.showDialog();

    // The native <dialog> restores focus on close to whatever was focused when it
    // opened. If that's our trigger, the restored focus fires `focusin` again and
    // reopens the modal. Drop focus from the trigger now (synchronously, before
    // the modal grabs it) so close() has nothing here to restore to — the modal
    // moves focus to its own input on open regardless.
    if (this.contains(document.activeElement)) document.activeElement.blur();
  };

  onOpen = () => {
    this.requested = false;
  };

  onClose = () => {
    this.requested = false;
    this.cooldown = true;
    setTimeout(() => {
      this.cooldown = false;
    }, 400);
  };
}

if (!customElements.get('search-bar')) {
  customElements.define('search-bar', SearchBar);
}
